import React from 'react';
import styles from './LimitReachedModal.module.css';

export interface LimitReachedModalProps {
  isOpen: boolean;
  onClose: () => void;
  quotaType: 'suggestions' | 'transcription';
  resetAt: Date;
}

export const LimitReachedModal: React.FC<LimitReachedModalProps> = ({
  isOpen,
  onClose,
  quotaType,
  resetAt,
}) => {
  if (!isOpen) return null;

  const formatResetTime = () => {
    const now = new Date();
    const diff = resetAt.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const quotaInfo = {
    suggestions: {
      title: 'AI Suggestions Limit Reached',
      icon: '💡',
      limit: '10 suggestions per day',
      benefit: 'Unlimited AI suggestions',
    },
    transcription: {
      title: 'Transcription Limit Reached',
      icon: '🎙️',
      limit: '30 minutes per day',
      benefit: 'Unlimited transcription time',
    },
  };

  const info = quotaInfo[quotaType];

  return (
    <div className={styles.overlay} onClick={onClose} role="presentation">
      <div 
        className={styles.modal} 
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <div className={styles.header}>
          <span className={styles.icon} aria-hidden="true">{info.icon}</span>
          <h2 id="modal-title" className={styles.title}>{info.title}</h2>
        </div>

        <div id="modal-description" className={styles.content}>
          <p className={styles.message}>
            You&apos;ve reached your free tier limit of <strong>{info.limit}</strong>.
          </p>

          <div className={styles.resetInfo}>
            <div className={styles.resetLabel}>Your quota will reset in:</div>
            <div className={styles.resetTime}>{formatResetTime()}</div>
            <div className={styles.resetSubtext}>Limits reset at midnight UTC</div>
          </div>

          <div className={styles.premiumSection}>
            <div className={styles.premiumBadge}>
              <span className={styles.premiumIcon} aria-hidden="true">⭐</span>
              <span className={styles.premiumTitle}>Upgrade to Premium</span>
            </div>
            <ul className={styles.benefitsList}>
              <li className={styles.benefit}>
                <span className={styles.checkIcon} aria-hidden="true">✓</span>
                {info.benefit}
              </li>
              <li className={styles.benefit}>
                <span className={styles.checkIcon} aria-hidden="true">✓</span>
                No daily limits
              </li>
              <li className={styles.benefit}>
                <span className={styles.checkIcon} aria-hidden="true">✓</span>
                Priority support
              </li>
            </ul>
            <p className={styles.premiumNote}>
              Contact support for premium tier upgrade information.
            </p>
          </div>
        </div>

        <div className={styles.footer}>
          <button className={styles.closeButton} onClick={onClose}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};
